import { useState, useEffect, useRef } from "react";
import type { TimelineEvent, TimelineEra, TimelinePerson } from "@/hooks/useTimelineData";

type ContentItem =
  | { type: "event"; item: TimelineEvent }
  | { type: "era"; item: TimelineEra }
  | { type: "person"; item: TimelinePerson };

type Props = {
  content: ContentItem;
  x: number;
  y: number;
  canEdit: boolean;
  onClose: () => void;
  onSave: (type: string, id: string, data: Record<string, unknown>) => Promise<void>;
};

const TYPE_COLORS: Record<string, { bg: string; border: string; text: string }> = {
  event: { bg: "bg-teal-500/20", border: "border-teal-500/40", text: "text-teal-400" },
  era: { bg: "bg-purple-500/20", border: "border-purple-500/40", text: "text-purple-400" },
  person: { bg: "bg-amber-500/20", border: "border-amber-500/40", text: "text-amber-400" },
};

const POPUP_WIDTH = 480;
const POPUP_MAX_HEIGHT = 500;

export function ContentDetailPopup({ content, x, y, canEdit, onClose, onSave }: Props) {
  const popupRef = useRef<HTMLDivElement>(null);

  const [isEditing, setIsEditing] = useState(false);
  const [editedBody, setEditedBody] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Calculate popup position with edge detection
  const [position, setPosition] = useState({ left: 0, top: 0 });

  useEffect(() => {
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let left = x + 20;
    let top = y - 100;

    if (left + POPUP_WIDTH > viewportWidth - 20) {
      left = x - POPUP_WIDTH - 20;
    }
    if (left < 20) {
      left = 20;
    }
    if (top + POPUP_MAX_HEIGHT > viewportHeight - 20) {
      top = viewportHeight - POPUP_MAX_HEIGHT - 20;
    }
    if (top < 20) {
      top = 20;
    }

    setPosition({ left, top });
  }, [x, y]);

  // Initialize edit body
  useEffect(() => {
    const body = getBody();
    setEditedBody(body);
  }, [content]);

  // Handle click outside
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (popupRef.current && !popupRef.current.contains(e.target as Node)) {
        onClose();
      }
    }

    function handleEscape(e: KeyboardEvent) {
      if (e.key === "Escape") {
        onClose();
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [onClose]);

  const getTitle = () => {
    if (content.type === "person") {
      return (content.item as TimelinePerson).name;
    }
    return content.item.title;
  };

  const getBody = () => {
    if (content.type === "person") {
      return (content.item as TimelinePerson).bio || "";
    }
    return content.item.description || "";
  };

  const getMetadata = () => {
    if (content.type === "event") {
      const event = content.item as TimelineEvent;
      return { Date: event.date?.split("T")[0] || "Unknown" };
    } else if (content.type === "era") {
      const era = content.item as TimelineEra;
      return { Period: `${era.startDate?.split("T")[0]} to ${era.endDate?.split("T")[0]}` };
    } else {
      const person = content.item as TimelinePerson;
      return {
        Lifespan: `${person.birthDate?.split("T")[0] || "?"} – ${person.deathDate?.split("T")[0] || "present"}`,
      };
    }
  };

  const getTypeLabel = () => {
    switch (content.type) {
      case "event": return "Event";
      case "era": return "Era";
      case "person": return "Person";
      default: return "Content";
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);
    try {
      const fieldName = content.type === "person" ? "bio" : "description";
      await onSave(content.type, content.item.id, { [fieldName]: editedBody });
      setIsEditing(false);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save");
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setEditedBody(getBody());
    setIsEditing(false);
    setError(null);
  };

  const typeColors = TYPE_COLORS[content.type] || TYPE_COLORS.event;
  const metadata = getMetadata();

  return (
    <div
      ref={popupRef}
      className="fixed z-50 bg-surface border border-border rounded-lg shadow-2xl overflow-hidden"
      style={{ left: position.left, top: position.top, width: POPUP_WIDTH, maxHeight: POPUP_MAX_HEIGHT }}
    >
      {/* Header */}
      <div className={`px-4 py-3 border-b ${typeColors.border} ${typeColors.bg}`}>
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className={`px-2 py-0.5 text-xs rounded font-medium ${typeColors.bg} ${typeColors.text} border ${typeColors.border}`}>
                {getTypeLabel()}
              </span>
            </div>
            <h3 className="text-base font-medium text-foreground">{getTitle()}</h3>
            {/* Metadata */}
            <div className="flex flex-wrap gap-3 mt-2 text-xs text-muted">
              {Object.entries(metadata).map(([key, value]) => (
                <span key={key}>
                  {key}: <span className="text-foreground/70">{value}</span>
                </span>
              ))}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1 text-muted hover:text-foreground hover:bg-border rounded transition-colors flex-shrink-0"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="mx-4 mt-3 px-2 py-1.5 bg-red-500/10 border border-red-500/20 rounded text-red-400 text-xs">
          {error}
        </div>
      )}

      {/* Content */}
      <div className="p-4 overflow-y-auto" style={{ maxHeight: POPUP_MAX_HEIGHT - 180 }}>
        {isEditing ? (
          <textarea
            value={editedBody}
            onChange={(e) => setEditedBody(e.target.value)}
            className="w-full bg-surface-elevated border border-border rounded px-3 py-2 text-sm text-foreground resize-none focus:outline-none focus:border-primary-500"
            style={{ minHeight: 200 }}
            placeholder="Enter content..."
          />
        ) : (
          <div className="text-sm text-foreground/80 leading-relaxed whitespace-pre-wrap">
            {getBody() || <span className="italic text-muted">No content</span>}
          </div>
        )}
      </div>

      {/* Footer */}
      {canEdit && (
        <div className="px-4 py-3 border-t border-border flex items-center justify-end gap-2 bg-surface-elevated">
          {isEditing ? (
            <>
              <button
                onClick={handleCancel}
                disabled={isSaving}
                className="px-4 py-2 text-sm text-muted hover:text-foreground hover:bg-border rounded transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="px-5 py-2 bg-primary-500 hover:bg-primary-600 disabled:opacity-50 text-white text-sm font-medium rounded transition-colors"
              >
                {isSaving ? "Saving..." : "Save"}
              </button>
            </>
          ) : (
            <button
              onClick={() => setIsEditing(true)}
              className="px-4 py-2 text-sm text-muted hover:text-foreground hover:bg-border rounded transition-colors"
            >
              Edit Content
            </button>
          )}
        </div>
      )}
    </div>
  );
}
